/**
 * FolderVisibilityModal — DM-only modal for bulk-setting the `players`
 * frontmatter field across every note in a folder (optionally recursive).
 *
 * Opened by right-clicking a folder label in the lore tree. The modal shows:
 *   - The target folder path
 *   - An "Include subfolders" checkbox
 *   - A visibility mode dropdown (same options as the single-note editor)
 *   - Player checkboxes (when "Specific players…" is selected)
 *   - Apply / Cancel buttons
 *
 * On Apply, calls `bulkVisibility` from loreApi.ts, reports the result,
 * and fires `onApplied` so the parent can refresh the manifest.
 */
import { useCallback, useState } from 'react';
import { bulkVisibility, type BulkVisibilityResult } from '../lib/loreApi';
import './FolderVisibilityModal.css';

interface Props {
  /** The folder path being edited (e.g. "Locations" or "Lore/Factions"). */
  folder: string;
  /** Authoritative player folder names from players.json. */
  knownPlayers: string[];
  /** Called after a successful bulk update so the parent can refresh. */
  onApplied: () => void;
  /** Called when the modal is dismissed without applying. */
  onClose: () => void;
}

type VisMode = 'default' | 'all' | 'none' | 'list';

export default function FolderVisibilityModal({
  folder,
  knownPlayers,
  onApplied,
  onClose,
}: Props) {
  const [recursive, setRecursive] = useState(false);
  const [visMode, setVisMode] = useState<VisMode>('all');
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkVisibilityResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onApply = useCallback(async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      let players: { all: true } | { none: true } | { allowed: string[] } | null;
      if (visMode === 'all') players = { all: true };
      else if (visMode === 'none') players = { none: true };
      else if (visMode === 'list') players = { allowed: [...selectedPlayers] };
      else players = null; // default = remove players key

      const r = await bulkVisibility(folder, recursive, players);
      setResult(r);
      if (r.errors.length === 0) {
        onApplied();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [folder, recursive, visMode, selectedPlayers, onApplied]);

  return (
    <div className="fvm-overlay" onClick={onClose}>
      <div className="fvm" onClick={(e) => e.stopPropagation()}>
        <header className="fvm__header">
          <h3>Set visibility: {folder}</h3>
          <button type="button" className="fvm__close" onClick={onClose}>
            ✕
          </button>
        </header>

        <div className="fvm__body">
          <label className="fvm__recursive">
            <input
              type="checkbox"
              checked={recursive}
              onChange={(e) => setRecursive(e.target.checked)}
              disabled={busy}
            />
            Include subfolders
          </label>

          <label className="fvm__field">
            <span>Visibility</span>
            <select
              value={visMode}
              disabled={busy}
              onChange={(e) => setVisMode(e.target.value as VisMode)}
            >
              <option value="default">DM only (default)</option>
              <option value="all">All players</option>
              <option value="none">DM only (explicit)</option>
              <option value="list">Specific players…</option>
            </select>
          </label>

          {visMode === 'list' && (
            <div className="fvm__players">
              {knownPlayers.map((p) => (
                <label key={p}>
                  <input
                    type="checkbox"
                    checked={selectedPlayers.has(p)}
                    disabled={busy}
                    onChange={(e) => {
                      const next = new Set(selectedPlayers);
                      if (e.target.checked) next.add(p);
                      else next.delete(p);
                      setSelectedPlayers(next);
                    }}
                  />
                  {p}
                </label>
              ))}
              {knownPlayers.length === 0 && (
                <span className="fvm__hint">No players configured.</span>
              )}
            </div>
          )}

          {error && <div className="lore-error">{error}</div>}

          {result && (
            <div className="fvm__result">
              <strong>{result.updated}</strong> note{result.updated !== 1 ? 's' : ''} updated
              {result.skipped.length > 0 && (
                <span>, {result.skipped.length} skipped</span>
              )}
              {result.errors.length > 0 && (
                <div className="fvm__result-errors">
                  {result.errors.map((e, i) => (
                    <div key={i}>{e}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="fvm__footer">
          <button type="button" disabled={busy} onClick={onClose}>
            {result ? 'Done' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              className="fvm__apply"
              disabled={busy || (visMode === 'list' && selectedPlayers.size === 0)}
              onClick={onApply}
            >
              {busy ? 'Applying…' : 'Apply'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
